// Updated PromptLibraryModal.js - With improved count refreshing and filter reset
import React, { useState, useEffect, useRef } from 'react';
import { loadPrompts, deletePrompt, recordPromptUsage, updatePrompt } from '../promptLibraryUtils';
import { 
  getSystemPrompts, 
  recordSystemPromptUsage,
  initializeFromCsv,
  resetSystemPrompts
} from '../initPromptLibrary'; // Import from initPromptLibrary.js
import './PromptLibraryModal.css';

function PromptLibraryModal({ isOpen, onClose, onSelectPrompt }) {
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [sortBy, setSortBy] = useState('lastUsedAt'); // 'name', 'lastUsedAt'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]); // Modified to array for multiple tag selection
  const [activeTab, setActiveTab] = useState('myPrompts'); // Default to my prompts tab
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editContent, setEditContent] = useState(''); 
  const [editTags, setEditTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [promptCounts, setPromptCounts] = useState({ myPrompts: 0, allPrompts: 0 });
  
  // Add a ref to track previous state for the useEffect
  const prevState = useRef({
    activeTab: 'myPrompts',
    searchTerm: '',
    selectedTags: [],
    sortBy: 'lastUsedAt',
    sortOrder: 'desc'
  });
  
  // Add refs for dynamic sizing
  const modalContentRef = useRef(null);
  const contentPreviewRef = useRef(null);
  
  // Function to show a temporary message (error or success)
  const showTemporaryMessage = (message, isError = true) => {
    setErrorMessage(isError ? message : `✓ ${message}`);
    setTimeout(() => setErrorMessage(''), 3000);
  };
  
  // Helper function to filter prompts consistently
  const filterPrompts = (promptsList, tab, searchText, tagFilters) => {
    if (!promptsList || !Array.isArray(promptsList) || promptsList.length === 0) {
      console.log(`filterPrompts: Empty or invalid promptsList (${promptsList?.length || 0} items)`);
      return [];
    }
    
    const { plainSearch, tagSearch } = parseSearchTerm(searchText || '');
    
    const filtered = promptsList.filter(p => {
      // First filter by tab
      if (tab === 'myPrompts') {
        if (!p.tags || !p.tags.some(t => t.toLowerCase() === 'myprompts')) {
          return false;
        }
      }
      // Note: for 'allPrompts' tab, we don't filter by tag type - include all prompts
      
      // Then filter by search
      let matchesSearch = false;
      
      if (!searchText) {
        matchesSearch = true;
      } else if (tagSearch) {
        // Tag search
        matchesSearch = p.tags && p.tags.some(tag => 
          tag.toLowerCase() === tagSearch.toLowerCase() || 
          tag.toLowerCase().includes(tagSearch.toLowerCase())
        );
      } else {
        // Regular search
        matchesSearch = 
          p.name.toLowerCase().includes(plainSearch.toLowerCase()) ||
          p.content.toLowerCase().includes(plainSearch.toLowerCase()) ||
          (p.tags && p.tags.some(tag => 
            tag.toLowerCase().includes(plainSearch.toLowerCase())
          ));
      }
      
      // Filter by selected tags
      const tagsToFilter = tab === 'myPrompts' 
        ? tagFilters.filter(tag => tag.toLowerCase() !== 'myprompts')
        : tagFilters;
        
      const matchesTags = tagsToFilter.length === 0 || 
        (p.tags && tagsToFilter.every(selectedTag => 
          p.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
        ));
      
      return matchesSearch && matchesTags;
    });
    
    console.log(`filterPrompts: Tab=${tab}, Found ${filtered.length} prompts (from ${promptsList.length} total)`);
    return filtered;
  };
  
  // Helper function to count filtered prompts consistently
  const countFilteredPrompts = (promptsList, tabType, searchText, tagFilters) => {
    return filterPrompts(promptsList, tabType, searchText, tagFilters).length;
  };
  
  // Helper function to filter AND sort prompts consistently, exactly as the displayed list
  const filterAndSortPrompts = (promptsList, tab, searchText, tagFilters, sortField, sortDir) => {
    // First filter the prompts
    const filtered = filterPrompts(promptsList, tab, searchText, tagFilters);
    
    // Then sort them exactly like filteredAndSortedPrompts does
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch(sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'lastUsedAt':
          // For lastUsedAt, fall back to createdAt if lastUsedAt is null
          const aDate = a.lastUsedAt || a.createdAt || 0;
          const bDate = b.lastUsedAt || b.createdAt || 0;
          comparison = aDate - bDate; // Newer dates are larger numbers
          break;
        default:
          // Default to lastUsedAt/createdAt if field is unknown
          const aDefaultDate = a.lastUsedAt || a.createdAt || 0;
          const bDefaultDate = b.lastUsedAt || b.createdAt || 0;
          comparison = aDefaultDate - bDefaultDate;
      }
      
      // Apply sort direction (desc = newest first for dates)
      return sortDir === 'asc' ? comparison : -comparison;
    });
  };
  
  // NEW: Debug helper function to log the filtered prompts for each tab
  const debugFilteredPrompts = () => {
    // Check what prompts would appear in each tab with current filters
    const myPromptsFiltered = filterPrompts(prompts, 'myPrompts', searchTerm, selectedTags);
    const allPromptsFiltered = filterPrompts(prompts, 'allPrompts', searchTerm, selectedTags);
    
    console.log(`Filtered prompts - MyPrompts tab: ${myPromptsFiltered.length}, AllPrompts tab: ${allPromptsFiltered.length}`);
    
    if (myPromptsFiltered.length > 0) {
      console.log(`First prompt in MyPrompts would be: "${myPromptsFiltered[0].name}" (${myPromptsFiltered[0].id})`);
    }
    
    if (allPromptsFiltered.length > 0) {
      console.log(`First prompt in AllPrompts would be: "${allPromptsFiltered[0].name}" (${allPromptsFiltered[0].id})`);
    }
  };
  
  // Load prompts when modal opens
  useEffect(() => {
    if (isOpen) {
      // FIXED: Always clear filters when reopening the modal
      setSearchTerm('');
      setSelectedTags([]);
      setSelectedPromptId(null);
      // Always set to My Prompts tab when opening
      setActiveTab('myPrompts');
      
      loadLibraryPrompts();
    }
  }, [isOpen]);
  
  // NEW: Add a direct effect specifically for tab changes
  useEffect(() => {
    if (!isOpen || editMode || isLoading) return;
    
    // This effect runs ONLY when the activeTab changes
    console.log(`Tab change effect triggered. Current tab: ${activeTab}`);
    
    // Run our debug helper to see what prompts we have in each tab
    debugFilteredPrompts();
    
    // Directly select the first prompt in the new tab
    setTimeout(() => {
      console.log(`Tab-specific prompt selection for tab: ${activeTab}`);
      
      // Get filtered prompts for the current tab
      const filtered = filterPrompts(prompts, activeTab, searchTerm, selectedTags);
      
      // Sort them according to current sort settings
      const sorted = [...filtered].sort((a, b) => {
        let comparison = 0;
        
        switch(sortBy) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'lastUsedAt':
            // For lastUsedAt, fall back to createdAt if lastUsedAt is null
            const aDate = a.lastUsedAt || a.createdAt || 0;
            const bDate = b.lastUsedAt || b.createdAt || 0;
            comparison = aDate - bDate;
            break;
          default:
            // Default to lastUsedAt/createdAt
            const aDefaultDate = a.lastUsedAt || a.createdAt || 0;
            const bDefaultDate = b.lastUsedAt || b.createdAt || 0;
            comparison = aDefaultDate - bDefaultDate;
        }
        
        return sortOrder === 'asc' ? comparison : -comparison;
      });
      
      if (sorted.length > 0) {
        const firstPrompt = sorted[0];
        console.log(`TAB EFFECT: Selecting first prompt: "${firstPrompt.name}" (ID: ${firstPrompt.id})`);
        
        // Set selection
        setSelectedPromptId(firstPrompt.id);
        
        // Debug check if the prompt exists in prompts array
        const promptExists = prompts.some(p => p.id === firstPrompt.id);
        console.log(`Selected prompt exists in prompts array: ${promptExists}`);
      } else {
        console.log(`TAB EFFECT: No prompts to select in ${activeTab} tab`);
        setSelectedPromptId(null);
      }
    }, 100);
    
  }, [activeTab, isOpen, editMode, isLoading, prompts, searchTerm, selectedTags, sortBy, sortOrder]);
  
  // NEW: Add a dedicated useEffect for prompt auto-selection
  // This will run whenever prompts, activeTab, searchTerm, or selectedTags change
  useEffect(() => {
    // Skip if modal is not open or we're in edit mode
    if (!isOpen || editMode || isLoading) return;
    
    // Skip if the change was just about the activeTab (handled by other effect)
    const isTabChangeOnly = prevState.current && 
                          prevState.current.activeTab !== activeTab && 
                          prevState.current.searchTerm === searchTerm && 
                          prevState.current.selectedTags === selectedTags &&
                          prevState.current.sortBy === sortBy &&
                          prevState.current.sortOrder === sortOrder;
    
    if (isTabChangeOnly) {
      console.log("Skipping general auto-selection because tab change is handled separately");
      return;
    }
    
    console.log("General auto-selection effect triggered");
    console.log(`Current sort: ${sortBy} ${sortOrder}, Tab: ${activeTab}`);
    
    // Run our debug helper
    debugFilteredPrompts();
    
    // Give React time to finish applying all state changes (especially sorting)
    setTimeout(() => {
      // Get the filtered and sorted prompts based on current state
      const filteredAndSorted = filterAndSortPrompts(prompts, activeTab, searchTerm, selectedTags, sortBy, sortOrder);
      
      if (filteredAndSorted.length > 0) {
        // Log the first few prompts to verify sort order
        const firstFew = filteredAndSorted.slice(0, Math.min(3, filteredAndSorted.length));
        console.log("First prompts in sorted list:");
        firstFew.forEach((p, i) => {
          const date = p.lastUsedAt || p.createdAt || 0;
          console.log(`${i+1}. "${p.name}" (ID: ${p.id}) - ${new Date(date).toISOString()}`);
        });
        
        // Select the first prompt
        console.log(`GENERAL EFFECT: Selecting first prompt: "${filteredAndSorted[0].name}" (ID: ${filteredAndSorted[0].id})`);
        setSelectedPromptId(filteredAndSorted[0].id);
      } else {
        console.log("No prompts available to select");
        setSelectedPromptId(null);
      }
    }, 100); // Increased delay to ensure sort is complete
    
    // Update the previous state for the next run
    prevState.current = {
      activeTab,
      searchTerm,
      selectedTags,
      sortBy,
      sortOrder
    };
  }, [isOpen, prompts, activeTab, searchTerm, selectedTags, sortBy, sortOrder, isLoading, editMode]);
  
  // On window resize, adjust the modal content height
  useEffect(() => {
    if (!isOpen) return;
    
    const adjustModalHeight = () => {
      if (modalContentRef.current) {
        const windowHeight = window.innerHeight;
        const maxHeight = Math.floor(windowHeight * 0.75); // 75% of window height
        modalContentRef.current.style.maxHeight = `${maxHeight}px`;
      }
    };
    
    // Set initial height
    adjustModalHeight();
    
    // Add resize listener
    window.addEventListener('resize', adjustModalHeight);
    
    return () => {
      window.removeEventListener('resize', adjustModalHeight);
    };
  }, [isOpen]);
  
  // IMPROVED: Dynamically adjust the content preview height when selected prompt changes
  useEffect(() => {
    if (contentPreviewRef.current && !editMode) {
      const container = contentPreviewRef.current.closest('.library-right-panel');
      if (container) {
        const headerHeight = container.querySelector('.prompt-header')?.offsetHeight || 0;
        const detailsHeight = container.querySelector('.prompt-details')?.offsetHeight || 0;
        
        // Get container height minus padding
        const containerHeight = container.offsetHeight - 40; // 20px padding top and bottom
        
        // Reserve space for tags section (fixed amount instead of dynamic calculation)
        const reservedTagsSpace = 120; // Reserve fixed space for tags section
        
        // Calculate max height for content with more conservative spacing
        const maxContentHeight = containerHeight - headerHeight - detailsHeight - reservedTagsSpace;
        
        // Set a reasonable height with a lower minimum
        contentPreviewRef.current.style.maxHeight = `${Math.max(120, maxContentHeight)}px`;
        
        console.log('Content sizing:', {
          containerHeight,
          headerHeight,
          detailsHeight,
          reservedTagsSpace,
          maxContentHeight
        });
      }
    }
  }, [selectedPromptId, editMode]);
  
  // Main function to load all prompts with proper error handling
  const loadLibraryPrompts = async () => {
    setIsLoading(true);
    try {
      console.log('Loading prompt library...');
      
      // First check if system prompts are initialized, if not initialize them
      const systemPrompts = getSystemPrompts();
      if (!systemPrompts || systemPrompts.length === 0) {
        console.log('System prompts not found, initializing...');
        const initialized = await initializeFromCsv();
        if (!initialized) {
          showTemporaryMessage('Failed to load system prompts. Trying again...', true);
          // Try one more time with force reload
          await resetSystemPrompts();
        }
      }
      
      // Load ALL prompts, including system-provided ones
      const allPrompts = loadAllPrompts();
      console.log("Loaded prompts:", allPrompts.length);
      
      // Clear any existing search or tags when first opening
      setSearchTerm('');
      setSelectedTags([]);
      setEditMode(false);
      setErrorMessage('');
      setSelectedPromptId(null); // Explicitly clear selection
      
      // Update state with the loaded prompts
      setPrompts(allPrompts);
      
      // Update prompt counts
      updatePromptCounts(allPrompts);
      
      // We'll select the first prompt AFTER the state updates are processed
      // This is done in a separate useEffect that responds to prompts changes
    } catch (error) {
      console.error('Error loading prompt library:', error);
      showTemporaryMessage('Error loading prompts. Please try again.', true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get the selected prompt object
  const selectedPrompt = selectedPromptId 
    ? prompts.find(p => p.id === selectedPromptId) 
    : null;
  
  // IMPROVED: Function to load ALL prompts including system prompts
  const loadAllPrompts = () => {
    // First get user prompts from loadPrompts()
    const userPrompts = loadPrompts();
    
    // Then get system prompts (the 300+ pre-supplied ones)
    const systemPrompts = getSystemPrompts();
    
    console.log("Loaded system prompts:", systemPrompts.length);
    console.log("Loaded user prompts:", userPrompts.length);
    
    // Combine both sets of prompts
    return [...userPrompts, ...systemPrompts];
  };
  
  // Update prompt counts for tabs
  const updatePromptCounts = (allPrompts) => {
    const myPromptCount = allPrompts.filter(p => 
      p.tags && p.tags.some(t => t.toLowerCase() === 'myprompts')
    ).length;
    
    setPromptCounts({
      myPrompts: myPromptCount,
      allPrompts: allPrompts.length
    });
  };

  // IMPROVED: Update counts based on current filters
  const updateTabCounts = (searchText, tagFilters) => {
    // Clone the prompts array to ensure we're working with the current data
    const currentPrompts = [...prompts];
    
    // Count prompts for My Prompts tab with current filters
    const myPromptsCount = countFilteredPrompts(currentPrompts, 'myPrompts', searchText, tagFilters);
    
    // Count prompts for All Prompts tab with current filters
    const allPromptsCount = countFilteredPrompts(currentPrompts, 'allPrompts', searchText, tagFilters);
    
    // Update the state with new counts
    setPromptCounts({
      myPrompts: myPromptsCount,
      allPrompts: allPromptsCount
    });
  };
  
  // Get all unique tags from all prompts and ensure they're sorted
  const getAllTags = () => {
    // Get ALL prompts including system prompts
    const allPrompts = loadAllPrompts();
    const tagSet = new Set();
    
    // First add tag "MyPrompts" if it exists in any prompt
    const hasMyPrompts = allPrompts.some(p => p.tags && p.tags.some(t => 
      t.toLowerCase() === 'myprompts'
    ));
    
    if (hasMyPrompts) {
      tagSet.add('MyPrompts');
    }
    
    // Then add all other tags from all prompts
    allPrompts.forEach(prompt => {
      if (prompt.tags && Array.isArray(prompt.tags)) {
        prompt.tags.forEach(tag => {
          tagSet.add(tag);
        });
      }
    });
    
    // Sort alphabetically but ensure MyPrompts is first
    const sortedTags = Array.from(tagSet).sort((a, b) => {
      if (a === 'MyPrompts') return -1;
      if (b === 'MyPrompts') return 1;
      return a.localeCompare(b);
    });
    
    console.log("All available tags:", sortedTags);
    return sortedTags;
  };
  
  // Parse #tag format searches and handle them specially
  const parseSearchTerm = (term) => {
    if (!term) return { plainSearch: '', tagSearch: null };
    
    // Look for #tag formats
    const tagMatch = term.match(/^#([a-zA-Z0-9_-]+)$/);
    if (tagMatch) {
      return { 
        plainSearch: '', 
        tagSearch: tagMatch[1].toLowerCase() 
      };
    }
    
    return { 
      plainSearch: term, 
      tagSearch: null 
    };
  };
  
  // IMPROVED: Handle tab change with proper count updates and auto-selection
  const handleTabChange = (tab) => {
    // Store previous selected prompt ID to help with debugging
    const prevSelectedId = selectedPromptId;
    console.log(`Tab changing from ${activeTab} to ${tab}, previous selected prompt: ${prevSelectedId}`);
    
    // First clear the selection to avoid confusion
    setSelectedPromptId(null);
    
    // Then update the active tab
    setActiveTab(tab);
    
    // Update counts for the newly selected tab
    updateTabCounts(searchTerm, selectedTags);
    
    // Force selection of first prompt after tab change with higher priority
    setTimeout(() => {
      // Instead of waiting for the useEffect, directly select here for tab changes
      console.log(`DIRECT SELECTION after tab change to ${tab}`);
      
      // Get filtered prompts for the NEW tab
      const filtered = filterPrompts(prompts, tab, searchTerm, selectedTags);
      
      // Sort them exactly like the main list
      const sorted = [...filtered].sort((a, b) => {
        let comparison = 0;
        
        switch(sortBy) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'lastUsedAt':
            // For lastUsedAt, fall back to createdAt if lastUsedAt is null
            const aDate = a.lastUsedAt || a.createdAt || 0;
            const bDate = b.lastUsedAt || b.createdAt || 0;
            comparison = aDate - bDate; // Newer dates are larger numbers
            break;
          default:
            // Default to lastUsedAt/createdAt if field is unknown
            const aDefaultDate = a.lastUsedAt || a.createdAt || 0;
            const bDefaultDate = b.lastUsedAt || b.createdAt || 0;
            comparison = aDefaultDate - bDefaultDate;
        }
        
        // Apply sort direction (desc = newest first for dates)
        return sortOrder === 'asc' ? comparison : -comparison;
      });
      
      if (sorted.length > 0) {
        console.log(`Tab ${tab} has ${sorted.length} prompts. First prompt: "${sorted[0].name}" (ID: ${sorted[0].id})`);
        console.log(`Setting selectedPromptId to ${sorted[0].id}`);
        
        // Actually set the selection
        setSelectedPromptId(sorted[0].id);
      } else {
        console.log(`Tab ${tab} has no prompts to select`);
      }
    }, 50);
  };
  
  // IMPROVED: Toggle tag selection and immediately refresh counts
  const handleSelectTag = (tag) => {
    console.log(`Tag selection changing: ${tag}`);
    
    // Clear selection first
    setSelectedPromptId(null);
    
    setSelectedTags(prevTags => {
      let updatedTags;
      // If tag is already selected, remove it (case insensitive check)
      if (prevTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
        updatedTags = prevTags.filter(t => t.toLowerCase() !== tag.toLowerCase());
      } else {
        // Otherwise, add the tag
        updatedTags = [...prevTags, tag];
      }
      
      // Immediately update counts with the new tags
      updateTabCounts(searchTerm, updatedTags);
      
      // Auto-selection is now handled by the useEffect that watches for selectedTags changes
      return updatedTags;
    });
  };
  
  // IMPROVED: Remove a specific tag and immediately refresh counts
  const handleRemoveTag = (tag) => {
    console.log(`Removing tag: ${tag}`);
    
    // Clear selection first
    setSelectedPromptId(null);
    
    // Always allow removing any tag from search filters, including MyPrompts
    const updatedTags = selectedTags.filter(t => t !== tag);
    setSelectedTags(updatedTags);
    
    // Immediately update counts
    updateTabCounts(searchTerm, updatedTags);
    
    // Auto-selection is now handled by the useEffect that watches for selectedTags changes
  };
  
  // Handle sorting
  const handleSort = (field) => {
    console.log(`Sort changing to: ${field}`);
    
    // Clear selection first
    setSelectedPromptId(null);
    
    // Update sort settings
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to appropriate order
      setSortBy(field);
      // Default to descending for date fields, ascending for name
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
    
    // Auto-selection is now handled by the useEffect that watches for sort changes
  };
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    // Show relative time for recent dates
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      // Show actual date for older dates
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };
  
  // Calculate counts for tab badges
  const countPrompts = (tab) => {
    const { plainSearch, tagSearch } = parseSearchTerm(searchTerm);
    
    return prompts.filter(p => {
      // First filter by tab - if in MyPrompts tab, only show user prompts
      if (tab === 'myPrompts') {
        if (!p.tags || !p.tags.some(t => t.toLowerCase() === 'myprompts')) {
          return false;
        }
      }
      
      // Then filter by search term (with improved tag search)
      let matchesSearch = false;
      
      if (!searchTerm) {
        matchesSearch = true;
      } else if (tagSearch) {
        // If tag search (#tag format), only match tags
        matchesSearch = p.tags && p.tags.some(tag => 
          tag.toLowerCase() === tagSearch.toLowerCase() || 
          tag.toLowerCase().includes(tagSearch.toLowerCase())
        );
      } else {
        // Regular search - match name, content, or tags
        matchesSearch = 
          p.name.toLowerCase().includes(plainSearch.toLowerCase()) ||
          p.content.toLowerCase().includes(plainSearch.toLowerCase()) ||
          (p.tags && p.tags.some(tag => 
            tag.toLowerCase().includes(plainSearch.toLowerCase())
          ));
      }
      
      // Filter by selected tags
      const tagsToFilter = tab === 'myPrompts'
        ? selectedTags.filter(tag => tag.toLowerCase() !== 'myprompts')
        : selectedTags;
        
      const matchesTags = tagsToFilter.length === 0 || 
        (p.tags && tagsToFilter.every(selectedTag => 
          p.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
        ));
      
      return matchesSearch && matchesTags;
    }).length;
  };
  
  // IMPROVED: Immediately update counts on search and auto-select first prompt
  const handleSearchInput = (e) => {
    const value = e.target.value;
    console.log(`Search term changing to: "${value}"`);
    
    // Clear selection first
    setSelectedPromptId(null);
    
    // Update search term
    setSearchTerm(value);
    
    // Immediately update counts with new search term
    updateTabCounts(value, selectedTags);
    
    // Auto-selection is now handled by the useEffect that watches for searchTerm changes
  };
  
  // IMPROVED: Clear search and filters with count update and auto-selection
  const handleClearSearch = () => {
    console.log("Clearing all filters");
    
    // Clear selection first
    setSelectedPromptId(null);
    
    // Then clear filters
    setSearchTerm('');
    setSelectedTags([]);
    
    // Update counts when filters are cleared
    updateTabCounts('', []);
    
    // Auto-selection is now handled by the useEffect that watches for filter changes
  };
  
  // Improved filtering logic to correctly handle the All Prompts tab
  const filteredAndSortedPrompts = [...prompts]
    .filter(prompt => {
      // Parse search term to check for #tag format
      const { plainSearch, tagSearch } = parseSearchTerm(searchTerm);
      
      // First filter by tab - ONLY if in MyPrompts tab
      if (activeTab === 'myPrompts') {
        if (!prompt.tags || !prompt.tags.some(t => t.toLowerCase() === 'myprompts')) {
          return false;
        }
      }
      
      // Then filter by search term with improved tag search
      let matchesSearch = false;
      
      if (!searchTerm) {
        matchesSearch = true;
      } else if (tagSearch) {
        // If using #tag format, only match against tags (case insensitive)
        matchesSearch = prompt.tags && prompt.tags.some(tag => 
          tag.toLowerCase() === tagSearch.toLowerCase() || 
          tag.toLowerCase().includes(tagSearch.toLowerCase())
        );
      } else {
        // Normal search (no # prefix)
        matchesSearch = 
          prompt.name.toLowerCase().includes(plainSearch.toLowerCase()) ||
          prompt.content.toLowerCase().includes(plainSearch.toLowerCase()) ||
          (prompt.tags && prompt.tags.some(tag => 
            tag.toLowerCase().includes(plainSearch.toLowerCase())
          ));
      }
      
      // Then filter by selected tags (must have ALL selected tags) - case insensitive
      // EXCEPT for the MyPrompts tag when already in My Prompts tab
      const tagsToFilter = activeTab === 'myPrompts' 
        ? selectedTags.filter(tag => tag.toLowerCase() !== 'myprompts')
        : selectedTags;
        
      const matchesTags = tagsToFilter.length === 0 || 
        (prompt.tags && tagsToFilter.every(selectedTag => 
          prompt.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
        ));
      
      return matchesSearch && matchesTags;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch(sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'lastUsedAt':
          // For lastUsedAt, fall back to createdAt if lastUsedAt is null
          const aDate = a.lastUsedAt || a.createdAt || 0;
          const bDate = b.lastUsedAt || b.createdAt || 0;
          comparison = aDate - bDate; // Newer dates are larger numbers
          break;
        default:
          // Default to sorting by lastUsedAt/createdAt
          const aDefaultDate = a.lastUsedAt || a.createdAt || 0;
          const bDefaultDate = b.lastUsedAt || b.createdAt || 0;
          comparison = aDefaultDate - bDefaultDate;
      }
      
      // For date sorts, we want newest first when descending
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
  // Handle selecting a prompt
  const handleSelectPrompt = () => {
    if (selectedPromptId) {
      const prompt = prompts.find(p => p.id === selectedPromptId);
      if (prompt) {
        // Handle different types of prompts (system vs user)
        if (prompt.isSystemPrompt) {
          recordSystemPromptUsage(selectedPromptId);
        } else {
          recordPromptUsage(selectedPromptId);
        }
        
        // Update the local state to reflect usage
        setPrompts(prevPrompts => 
          prevPrompts.map(p => 
            p.id === selectedPromptId 
              ? { 
                  ...p, 
                  lastUsedAt: Date.now(), 
                  useCount: (p.useCount || 0) + 1 
                } 
              : p
          )
        );
        
        onSelectPrompt(prompt.content);
        onClose();
      }
    }
  };
  
  // IMPROVED: Handle deleting a prompt with count updates
  const handleDeletePrompt = (id, e) => {
    e.stopPropagation();
    
    // Check if this is a system prompt
    const promptToDelete = prompts.find(p => p.id === id);
    if (promptToDelete && promptToDelete.isSystemPrompt) {
      showTemporaryMessage("System prompts cannot be deleted", true);
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      deletePrompt(id);
      
      // Update prompts state
      const updatedPrompts = prompts.filter(p => p.id !== id);
      setPrompts(updatedPrompts);
      
      if (selectedPromptId === id) {
        // If the deleted prompt was selected, select another prompt
        setTimeout(() => {
          console.log("Prompt deleted, auto-selecting a new prompt...");
          const filteredPrompts = filterPrompts(updatedPrompts, activeTab, searchTerm, selectedTags);
          if (filteredPrompts.length > 0) {
            console.log(`Auto-selecting first prompt: ${filteredPrompts[0].name}`);
            setSelectedPromptId(filteredPrompts[0].id);
          } else {
            console.log("No prompts available to select after deletion");
            setSelectedPromptId(null);
          }
        }, 10);
      }
      
      // Update prompt counts after deletion
      updateTabCounts(searchTerm, selectedTags);
    }
  };
  
  // Function to handle reload of system prompts
  const handleReloadSystemPrompts = async () => {
    setIsLoading(true);
    try {
      const result = await resetSystemPrompts();
      if (result) {
        // Reload all prompts
        const allPrompts = loadAllPrompts();
        setPrompts(allPrompts);
        updateTabCounts(searchTerm, selectedTags);
        
        // Auto-select the first prompt after reload
        setTimeout(() => {
          console.log("System prompts reloaded, auto-selecting first prompt...");
          const filteredPrompts = filterPrompts(allPrompts, activeTab, searchTerm, selectedTags);
          if (filteredPrompts.length > 0) {
            console.log(`Auto-selecting first prompt: ${filteredPrompts[0].name}`);
            setSelectedPromptId(filteredPrompts[0].id);
          } else {
            console.log("No prompts available to select after reload");
            setSelectedPromptId(null);
          }
        }, 10);
        
        showTemporaryMessage("System prompts reloaded successfully!", false);
      } else {
        showTemporaryMessage("Failed to reload system prompts", true);
      }
    } catch (error) {
      console.error("Error reloading system prompts:", error);
      showTemporaryMessage("Error reloading system prompts", true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Start editing a prompt
  const handleEditPrompt = (e) => {
    e.stopPropagation();
    if (selectedPrompt) {
      // Prevent editing system prompts
      if (selectedPrompt.isSystemPrompt) {
        showTemporaryMessage("System prompts cannot be edited", true);
        return;
      }
      
      setEditName(selectedPrompt.name);
      setEditContent(selectedPrompt.content);
      setEditTags(selectedPrompt.tags || []);
      setEditTagInput('');
      setEditMode(true);
    }
  };
  
  // Save edited prompt
  const handleSaveEdit = () => {
    if (!editName.trim()) {
      showTemporaryMessage('Prompt name cannot be empty', true);
      return;
    }
    
    if (!editContent.trim()) {
      showTemporaryMessage('Prompt content cannot be empty', true);
      return;
    }
    
    // Make sure MyPrompts tag is preserved if it was there originally
    let finalTags = [...editTags];
    if (selectedPrompt?.tags?.includes('MyPrompts') && !finalTags.includes('MyPrompts')) {
      finalTags = ['MyPrompts', ...finalTags];
    }
    
    try {
      const updatedPrompt = updatePrompt(selectedPromptId, {
        name: editName.trim(),
        content: editContent.trim(),
        tags: finalTags
      });
      
      if (updatedPrompt) {
        // Update local state
        setPrompts(prevPrompts => 
          prevPrompts.map(p => 
            p.id === selectedPromptId 
              ? { 
                  ...p, 
                  name: editName.trim(), 
                  content: editContent.trim(),
                  tags: finalTags
                } 
              : p
          )
        );
        setEditMode(false);
        
        // Update counts after editing
        updateTabCounts(searchTerm, selectedTags);
        
        showTemporaryMessage('Prompt updated successfully!', false);
      } else {
        showTemporaryMessage('Failed to update prompt', true);
      }
    } catch (error) {
      console.error("Error updating prompt:", error);
      showTemporaryMessage(`Error: ${error.message || 'Failed to update prompt'}`, true);
    }
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    if (window.confirm('Discard your changes?')) {
      setEditMode(false);
    }
  };
  
  // Add a tag while editing
  const handleAddEditTag = () => {
    const trimmedTag = editTagInput.trim();
    
    if (!trimmedTag) {
      return;
    }
    
    // Check if tag already exists
    if (editTags.includes(trimmedTag)) {
      showTemporaryMessage('This tag already exists on the prompt', true);
      return;
    }
    
    // Add the new tag
    setEditTags([...editTags, trimmedTag]);
    setEditTagInput('');
  };
  
  // Remove a tag while editing
  const handleRemoveEditTag = (tagToRemove) => {
    // Don't allow removing MyPrompts from prompts that already have it
    if (tagToRemove === 'MyPrompts' && selectedPrompt?.tags?.includes('MyPrompts')) {
      showTemporaryMessage("'MyPrompts' tag cannot be removed", true);
      return;
    }
    
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };
  
  // Handle key press while editing tag input
  const handleEditTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEditTag();
    }
  };

  // Updated counts
  const myPromptsCount = promptCounts.myPrompts;
  const allPromptsCount = promptCounts.allPrompts;
  
  // FIXED: Check if there are any matching prompts
  const hasMatchingPrompts = filteredAndSortedPrompts.length > 0;
  
  // FIXED: Check if there are any user prompts (MyPrompts tag)
  const hasAnyUserPrompts = prompts.some(p => 
    p.tags && p.tags.some(t => t.toLowerCase() === 'myprompts')
  );
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target.className === 'modal-overlay') {
        if (editMode) {
          if (window.confirm('Discard your changes?')) {
            onClose();
          }
        } else {
          onClose();
        }
      }
    }}>
      <div className="library-modal-container">
        <div className="modal-header">
          <h3>Prompt Library</h3>
          <button 
            className="close-button" 
            onClick={() => {
              if (editMode) {
                if (window.confirm('Discard your changes?')) {
                  onClose();
                }
              } else {
                onClose();
              }
            }}
          >
            ×
          </button>
        </div>
        
        <div className="library-tabs">
          <button 
            className={`library-tab ${activeTab === 'myPrompts' ? 'active' : ''}`}
            onClick={() => handleTabChange('myPrompts')}
          >
            My Prompts
            <span className={`tab-count ${myPromptsCount > 0 ? 'non-zero' : ''}`}>
              {myPromptsCount}
            </span>
          </button>
          <button 
            className={`library-tab ${activeTab === 'allPrompts' ? 'active' : ''}`}
            onClick={() => handleTabChange('allPrompts')}
          >
            All Prompts
            <span className={`tab-count ${allPromptsCount > 0 ? 'non-zero' : ''}`}>
              {allPromptsCount}
            </span>
          </button>
        </div>
        
        <div className="library-search-bar">
          <input
            type="text"
            placeholder="Search by name, content, or #tag"
            value={searchTerm}
            onChange={handleSearchInput}
            className="library-search-input"
          />
          {(searchTerm || (selectedTags.length > 0 && !(selectedTags.length === 1 && selectedTags[0] === 'MyPrompts' && activeTab === 'myPrompts'))) && (
            <button 
              className="clear-search-btn"
              onClick={handleClearSearch}
              title="Clear all filters"
            >
              ×
            </button>
          )}
          
          {(searchTerm || selectedTags.length > (activeTab === 'myPrompts' ? 1 : 0)) && (
            <button 
              className="clear-all-filters-btn"
              onClick={handleClearSearch}
            >
              Clear all filters
            </button>
          )}
          
         
        </div>
        
        {/* Active filters row - only show if there are filters */}
        {selectedTags.length > 0 && (
          <div className="active-filters-row">
            {selectedTags.map(tag => (
              <div key={tag} className="active-filter-tag">
                #{tag}
                <button 
                  className="remove-filter-btn"
                  onClick={() => handleRemoveTag(tag)}
                  title={`Remove ${tag} filter`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Tag buttons row with Existing Tags label */}
        <div className="tags-label-container">
          <div className="tags-label">
            Existing Tags (Click to Select) 
          </div>
          <div className="library-tags-row">
            {getAllTags().map(tag => (
              <button
                key={tag}
                onClick={() => handleSelectTag(tag)}
                className={`library-tag-button ${selectedTags.includes(tag) ? 'active' : ''}`}
              >
                #{tag}
                {selectedTags.includes(tag) && (
                  <span className="tag-remove-icon">×</span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        <div className="library-modal-content" ref={modalContentRef}>
          {errorMessage && (
            <div className={errorMessage.includes("successfully") ? "modal-success" : "modal-error"}>
              {errorMessage}
            </div>
          )}
          
          <div className="library-left-panel">
            {/* Sort controls - simplified */}
            <div className="library-sort-controls">
              <span>Sort by:</span>
              <button 
                className={`sort-button ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => handleSort('name')}
              >
                Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button 
                className={`sort-button ${sortBy === 'lastUsedAt' ? 'active' : ''}`}
                onClick={() => handleSort('lastUsedAt')}
              >
                Recent {sortBy === 'lastUsedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
            
            {/* Prompt list - card layout */}
            <div className="prompt-cards">
              {isLoading ? (
                <div className="loading-indicator">
                  Loading prompts...
                </div>
              ) : filteredAndSortedPrompts.length === 0 ? (
                <div className="no-prompts-message">
                  {/* FIXED: Improved message logic for no results */}
                  {searchTerm || selectedTags.length > 0
                    ? 'No prompts match your search or filters' 
                    : activeTab === 'myPrompts' && !hasAnyUserPrompts
                      ? 'No saved prompts yet - save your first prompt using the bookmark icon'
                      : 'No prompts available'}
                </div>
              ) : (
                filteredAndSortedPrompts.map(prompt => (
                  <div 
                    key={prompt.id}
                    className={`prompt-card ${selectedPromptId === prompt.id ? 'selected' : ''} ${prompt.isSystemPrompt ? 'system-prompt' : ''}`}
                    onClick={() => setSelectedPromptId(prompt.id)}
                  >
                    <div className="prompt-card-header">
                      <div className="prompt-card-name">{prompt.name}</div>
                      {!prompt.isSystemPrompt && (
                        <button 
                          className="delete-prompt-button"
                          onClick={(e) => handleDeletePrompt(prompt.id, e)}
                          title="Delete prompt"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="prompt-card-preview">
                      {prompt.content.length > 60 
                        ? prompt.content.substring(0, 60) + "..." 
                        : prompt.content}
                    </div>
                    <div className="prompt-card-footer">
                      <div className="prompt-card-date">
                        {prompt.lastUsedAt ? `Used ${formatDate(prompt.lastUsedAt)}` : `Created ${formatDate(prompt.createdAt)}`}
                      </div>
                      <div className="prompt-card-tags">
                        {prompt.tags && prompt.tags.slice(0, 2).map(tag => (
                          <span 
                            key={tag} 
                            className={`prompt-card-tag ${tag === 'MyPrompts' ? 'system-tag' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTag(tag);
                            }}
                          >
                            #{tag}
                          </span>
                        ))}
                        {prompt.tags && prompt.tags.length > 2 && (
                          <span className="more-tags">+{prompt.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="library-right-panel">
            {/* FIXED: Only show prompt preview if we have a selected prompt AND matching prompts */}
            {selectedPrompt && hasMatchingPrompts ? (
              <div className="prompt-preview">
                {editMode ? (
                  /* Edit mode UI - Improved tag container */
                  <div className="edit-prompt-form">
                    <div className="form-group">
                      <label htmlFor="edit-prompt-name">Prompt Name</label>
                      <input
                        id="edit-prompt-name"
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Enter a name for this prompt"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-prompt-content">Prompt Content</label>
                      <textarea
                        id="edit-prompt-content"
                        className="edit-prompt-content"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        placeholder="Enter prompt content"
                        rows={12}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Tags</label>
                      <div className="edit-tags-container">
                        <div className="edit-tags-list">
                          {editTags.map(tag => (
                            <div 
                              key={tag} 
                              className={`edit-tag-item ${tag === 'MyPrompts' ? 'system-tag' : ''}`}
                            >
                              <span>{tag}</span>
                              <button 
                                type="button" 
                                className="remove-edit-tag" 
                                onClick={() => handleRemoveEditTag(tag)}
                                disabled={tag === 'MyPrompts' && selectedPrompt?.tags?.includes('MyPrompts')}
                              >
                                {tag !== 'MyPrompts' || !selectedPrompt?.tags?.includes('MyPrompts') ? '×' : ''}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Tag input - completely separate from tags display */}
                      <div className="tag-input-container">
                        <input
                          type="text"
                          value={editTagInput}
                          onChange={(e) => setEditTagInput(e.target.value)}
                          onKeyDown={handleEditTagKeyDown}
                          placeholder="Add a tag (press Enter)"
                          className="edit-tag-input"
                        />
                        <button 
                          type="button"
                          className="add-tag-button"
                          onClick={handleAddEditTag}
                          disabled={!editTagInput.trim()}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="edit-actions">
                      <button 
                        className="cancel-edit-button"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <button 
                        className="save-edit-button"
                        onClick={handleSaveEdit}
                        disabled={!editName.trim() || !editContent.trim()}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View mode UI - with dynamic sizing for content */
                  <>
                    <div className="prompt-header">
                      <h4>{selectedPrompt.name}</h4>
                      {!selectedPrompt.isSystemPrompt && (
                        <button 
                          className="edit-prompt-button"
                          onClick={handleEditPrompt}
                          title="Edit prompt details"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    <div className="prompt-content" ref={contentPreviewRef}>
                      {selectedPrompt.content}
                    </div>
                    <div className="prompt-details">
                      <div>Created: {formatDate(selectedPrompt.createdAt)}</div>
                      <div>Last used: {formatDate(selectedPrompt.lastUsedAt)}</div>
                      {selectedPrompt.useCount > 0 && (
                        <div>Used {selectedPrompt.useCount} time{selectedPrompt.useCount !== 1 ? 's' : ''}</div>
                      )}
                      {selectedPrompt.isSystemPrompt && (
                        <div className="system-prompt-badge">System Prompt</div>
                      )}
                    </div>
                    
                    {/* STANDARDIZED: Tags section with consistent rendering for all prompt types */}
                    <div className="tags-section">
                      <div className="tags-label">Tags:</div>
                      <div className="tags-container">
                        {selectedPrompt.tags && selectedPrompt.tags.length > 0 ? (
                          selectedPrompt.tags.map(tag => (
                            <span 
                              key={tag} 
                              className={`tag-pill ${tag === 'MyPrompts' ? 'system-tag' : ''}`}
                              onClick={() => handleSelectTag(tag)}
                            >
                              #{tag}
                            </span>
                          ))
                        ) : (
                          <span className="no-tags">No tags</span>
                        )}
                      </div>
                      {/* Add a spacer div to ensure content doesn't get cut off */}
                      <div style={{ height: '10px' }}></div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="no-selection-message">
                <div className="no-selection-icon">📚</div>
                <div className="no-selection-text">
                  {/* FIXED: Improved no selection message logic */}
                  {filteredAndSortedPrompts.length > 0 
                    ? 'Select a prompt from the list to preview it' 
                    : searchTerm || selectedTags.length > 0
                      ? 'No prompts match your search criteria'
                      : activeTab === 'myPrompts' && !hasAnyUserPrompts
                        ? 'Save your own prompts using the bookmark icon in the input field'
                        : 'No prompts available'}
                </div>
              </div>
            )}
          </div>
        </div>
        
     <div className="modal-footer">
     {editMode ? (
    // Edit mode buttons
    <>
      <button className="cancel-button" onClick={handleCancelEdit}>
        Discard Changes
      </button>
      <button 
        className="select-button"
        onClick={handleSaveEdit}
        disabled={!editName.trim() || !editContent.trim()}
      >
        Save Changes
      </button>
    </>
  ) : (
    // Normal mode buttons
    <>
      <button className="cancel-button" onClick={onClose}>
        Cancel
      </button>
      <button 
        className="select-button"
        onClick={handleSelectPrompt}
        disabled={!selectedPromptId || editMode}
      >
        Use Selected Prompt
      </button>
    </>
  )}
</div>
      </div>
    </div>
  );
}

export default PromptLibraryModal;